<template>
    <div>
        <p class="profile-form__subtitle">
            <span class="fas fa-envelope"></span>
            {{ $t('Email address') }}
        </p>
        <div class="row">
            <div class="col-md-9 mb-2">
                <ui-input 
                id="email" 
                name="email"
                type="email"
                class="mb-2" 
                v-model="formData.email"
                :label="$t('The email address associated with your account')" 
                :disabled="!isEditing || step > 0"
                :field="v$.formData.email">
                    <template #append>
                        <div v-if="!isEditing" class="change-email-card__edit-button">
                            <button class="change-email-card__edit-button" @click="edit">
                                <span class="fas fa-edit"></span>
                            </button>
                        </div>
                        <div v-else-if="step > 0">
                            <button class="change-email-card__cancel-button" @click="cancel">
                                <span class="fas fa-times"></span>
                            </button>
                        </div>
                        <div v-else class="change-email-card__actions">
                            <button class="change-email-card__confirm-button" @click="validateEmail(formData.email)">
                                <span class="fas fa-check"></span>
                            </button>
                            <button class="change-email-card__cancel-button" @click="cancel">
                                <span class="fas fa-times"></span>
                            </button>
                        </div>
                    </template>
                </ui-input>
            </div>
            <div class="col-md-9 mb-2" v-if="step == 1">
                <ui-input 
                id="code" 
                name="code"
                type="text" 
                class="mb-2" 
                v-model="formData.code" 
                :label="$t('We sent a verification code to that email address. Please enter it below.')"
                :field="v$.formData.code"
                :disabled="validatingCode"
                >
                    <template #append>
                        <div>
                            <button :disabled="validatingCode" class="change-email-card__confirm-button" @click="handleValidateCode">
                                <span v-if="validatingCode" class="fas fa-spinner fa-spin" />
                                <span v-else class="fas fa-check"></span>
                            </button>
                        </div>
                    </template>
                </ui-input>
            </div>
            <div class="col-md-9 mb-2" v-if="step == 2">
                <p class="change-email-card__success-email">
                    <span class="fas fa-check-circle"></span>
                    {{ $t('Your email address has been successfully verified.') }}
                </p>
                <ui-input
                id="password"
                name="password"
                type="text"
                class="mb-2 mt-2"
                v-model="formData.password"
                :label="$t('To complete the change, enter the verification code we sent to your current email.')"
                :field="v$.formData.code"
                :disabled="validatingCode"
                >
                <template #append>
                    <div>
                        <button  class="change-email-card__confirm-button" @click="handleUpdateEmail" :disabled="updatingEmail">
                            <span v-if="updatingEmail" class="fas fa-spinner fa-spin" />
                            <span v-else class="fas fa-check"></span>
                        </button>
                    </div>
                </template>
                </ui-input>
            </div>
        </div>
        <vue-recaptcha
        class="w-100"
        ref="recaptchaComponent"
        size="invisible"
        loadRecaptchaScript
        :sitekey="CONST.CAPTCHACODE"
        @verify="sendNotification"
        />
        <vue-recaptcha
        class="w-100"
        ref="validateCode"
        size="invisible"
        loadRecaptchaScript
        :sitekey="CONST.CAPTCHACODE"
        @verify="validateCode"
        />
        <vue-recaptcha
        class="w-100"
        ref="updateEmail"
        size="invisible"
        loadRecaptchaScript
        :sitekey="CONST.CAPTCHACODE"
        @verify="updateEmail"
        />
    </div>
</template>

<script>
import UiInput from '@/components/ui/inputs/Input.vue';
import VueRecaptcha from 'vue-recaptcha';

import recaptchaUtils from '@/utils/recaptcha.utils';

import { useVuelidate } from '@vuelidate/core'
import { email, required, helpers, minLength, maxLength } from '@vuelidate/validators';

export default {
    name: 'ChangeEmailCard',
    components: { UiInput, VueRecaptcha },
    props: {
        account: {
            type: Object,
            required: true,
        },
    },
    watch: {
        'account.email'(newEmail) {
            this.formData.email = newEmail;
        },
    },
    emits: ['emailUpdated'],
    setup() {
        return {
            v$: useVuelidate(),
        };
    },
    validations() {
        return {
            formData: {
                email: {
                    required: helpers.withMessage(this.$t('validations.required', { field: this.$t('email') }), required),
                    email: helpers.withMessage(this.$t('Email invalid'), email),
                    $autoDirty: true,
                },
                code: {
                    required: helpers.withMessage(this.$t('validations.required', { field: this.$t('code') }), required),
                    $autoDirty: true,
                    minLength: helpers.withMessage(({ $params }) => this.$t('This field must be a minimum of {min} characters', $params ), minLength(6)),
                    maxLength: helpers.withMessage(({ $params }) => this.$t('This field must have a maximum of {max} characters', $params ), maxLength(6)),
                }
            }
        }
    },
    data() {
        return {
           isEditing: false,
           formData: {
               email: '',
               code: ''
           },
            originalEmail: this.account.email,
            errorMessage: '',
            vuelidateExternalResults:{
                formData: {
                    email: [],
                },
            },
            step: 0,
            validatingCode: false,
            updatingEmail: false,
        };
    },
    mounted() {
        this.formData.email = this.account.email;
    },
    methods: {
        edit() {
            this.isEditing = true;
        },
        cancel() {
            this.isEditing = false;
            this.formData.email = this.originalEmail;
            this.formData.code = '';
            this.formData.password = '';
            this.step = 0;
        },
        closeAll() {
            this.isEditing = false;
            this.formData.code = '';
            this.formData.password = '';
            this.step = 0;

        },
        setExternalError(error) {
            Object.assign(this.vuelidateExternalResults, error)
        },
        async validateEmail(value) {
            try {
                this.v$.formData.email.$touch();
                if (this.v$.formData.email.$invalid) {
                    return;
                }

                if (!value) {
                    return false;
                }

                if (this.formData.email === this.originalEmail) {
                    this.cancel();
                    return;
                }
                
                const config = { auth: { username: value, password: '' } };
                await this.axiosAccount.get('/api/accounts/validate/email', config);
                this.setExternalError({ formData: { email: [this.$t('This email already exist')] } });
            } catch(error) {
                if (error.response.status === 404) {
                    this.sendNotificationRecaptcha();
                    this.step = 1;
                    return;
                }
                if (error.response.status === 409) {
                    this.setExternalError({ formData: { email: [this.$t('Too many request. Please, try again.')] } });
                } 
                this.showError(error);
            }
        },
        sendNotificationRecaptcha() {
            if (!recaptchaUtils.isAvailable()) {
                this.sendNotification('');
                return;
            }

            this.$refs.recaptchaComponent.reset();
            this.$refs.recaptchaComponent.execute();
        },
        async sendNotification(token){
            try {
                const payload = { type: 'email', action: 'email_change', message: 'Change Email', from: 'Tendencys', to: this.formData.email, token };
                await this.axiosAccount.post('/api/notifications', payload);
            } catch (error) {
                this.showError(error);
            }
        },
        handleValidateCode() {
            this.v$.formData.code.$touch();
            if (this.v$.formData.code.$invalid) {
                return;
            }
            this.validatingCode = true;
            if (!recaptchaUtils.isAvailable()) {
                this.validateCode('');
                return;
            }

            this.$refs.validateCode.reset();
            this.$refs.validateCode.execute();
        },
        async validateCode(token) {
            try {
                let payload = { code: this.formData.code, token: token };
                const { data } = await this.axiosAccount.post('/api/notifications/code-verification/email', payload);
                if (data.success) {
                    this.step = 2;
                }
            } catch (error) {
                this.showError(error);
            } finally {
                this.validatingCode = false;
            }
        },
        handleUpdateEmail() {
            this.v$.formData.code.$touch();
            if (this.v$.formData.code.$invalid) {
                return;
            }
            this.updatingEmail = true;
            if (!recaptchaUtils.isAvailable()) {
                this.updateEmail('');
                return;
            }

            this.$refs.updateEmail.reset();
            this.$refs.updateEmail.execute();
        },
        async updateEmail(token) {
            try {
                const payload = { email: this.formData.email, code: this.formData.password, token };
                const { data } = await this.axiosAccount.put('/api/accounts/me/email', payload);
                this.$emit('emailUpdated', data);
                this.closeAll();
            } catch (err) {
                this.showError(err);
                this.cancel();
            } finally {
                this.updatingEmail = false;
            }
        }
    }
}

</script>


<style lang="scss">
.change-email-card {
    border: 1px solid #d9d9d9;
    border-radius: 8px;
    padding: 16px;

    &__title {
        color: $general-black;
        margin-bottom: 16px;
    }

    &__content {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 8px;
        width: 100%;
    }

    &__edit-button {
        align-items: center;
        background-color: transparent;
        border: none !important;
        display: flex;
        gap: 8px;
        height: 32px;
        padding: 8px 16px;
        color: $accounts-secondary-600 !important;

        &:hover {
            background-color: transparent !important;
            color: $white;
        }
    }

    &__actions {
        display: flex;
        align-items: center;
    }

    &__confirm-button {
        align-items: center;
        background-color: transparent;
        border: none !important;
        display: flex;
        gap: 8px;
        height: 32px;
        padding: 3px 9px;
        color: $accounts-secondary-600 !important;

        &:hover {
            background-color:transparent !important;
            color: #189771 !important;
        }
    }

    &__cancel-button {
        align-items: center;
        background-color: transparent;
        border: none !important;
        display: flex;
        gap: 8px;
        height: 32px;
        padding: 3px 9px;
        color: $accounts-secondary-600 !important;

        &:hover {
            background-color:transparent !important;
            color: #F44336 !important;
        }
    }

    &__success-email {
        border-radius: 8px;
        padding: 8px;
        text-align: left;
        width: 100%;
        font-size: 14px;
        color: #4E8327;
    }

}
</style>